import { useState } from "react"
import {
  type PipelinePhaseDto,
  type PipelineEventStatusDto,
} from "~/ui-rtk/api/pipelineEventApi"
import PipelineProgressBar, {
  PipelineProgressSummary,
} from "../PipelineProgressBar"

type PhaseStatus = PipelinePhaseDto["status"]

function getStatusBorderColor(status: PhaseStatus): string {
  switch (status) {
    case "failed":
      return "border-states-error/50"
    case "completed":
      return "border-states-success/50"
    default:
      return "border-basic-blue"
  }
}

function getPhaseHeaderBgColor(status: PhaseStatus): string {
  switch (status) {
    case "failed":
      return "bg-states-error/10"
    case "completed":
      return "bg-states-success/10"
    case "in_progress":
      return "bg-states-info/10"
    default:
      return "bg-basic-black"
  }
}

function getPhaseStatusTextColor(status: PhaseStatus): string {
  switch (status) {
    case "failed":
      return "text-states-error"
    case "completed":
      return "text-states-success"
    case "in_progress":
      return "text-states-info"
    default:
      return "text-basic-grey-inactive"
  }
}

function getPhaseStatusLabel(status: PhaseStatus): string {
  switch (status) {
    case "completed":
      return "✓ Complete"
    case "failed":
      return "✗ Failed"
    case "in_progress":
      return "◐ In Progress"
    default:
      return "○ Pending"
  }
}

type CompanyPipelineCardProps = {
  companyName: string
  phases: PipelinePhaseDto[]
  progressPercent: number
  overallStatus: "pending" | "in_progress" | "completed" | "failed"
  totalEvents: number
  lastEventAt?: string
  currentStage?: string
  failedStage?: string
}

function StatusBadge({
  status,
  currentStage,
  failedStage,
}: {
  status: CompanyPipelineCardProps["overallStatus"]
  currentStage?: string
  failedStage?: string
}) {
  if (status === "completed") {
    return (
      <span className="px-2 py-0.5 text-xs rounded bg-states-success/20 text-states-success">
        ✓ Complete
      </span>
    )
  }
  if (status === "failed") {
    return (
      <span className="px-2 py-0.5 text-xs rounded bg-states-error/20 text-states-error">
        ✗ Failed: {failedStage}
      </span>
    )
  }
  if (status === "in_progress") {
    return (
      <span className="px-2 py-0.5 text-xs rounded bg-states-info/20 text-states-info">
        ◐ {currentStage || "Processing"}
      </span>
    )
  }
  return (
    <span className="px-2 py-0.5 text-xs rounded bg-basic-blue text-basic-grey-inactive">
      ○ Pending
    </span>
  )
}

function EventStatusIcon({
  status,
}: {
  status: PipelineEventStatusDto["status"]
}) {
  if (status === "completed") {
    return (
      <svg
        className="w-4 h-4 text-states-success"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    )
  }
  if (status === "failed") {
    return (
      <svg
        className="w-4 h-4 text-states-error"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    )
  }
  return (
    <svg
      className="w-4 h-4 text-basic-grey-inactive"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="8" strokeWidth={2} />
    </svg>
  )
}

export default function CompanyPipelineCard({
  companyName,
  phases,
  progressPercent,
  overallStatus,
  totalEvents,
  lastEventAt,
  currentStage,
  failedStage,
}: CompanyPipelineCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const borderColor = getStatusBorderColor(overallStatus)

  return (
    <div className={`p-4 rounded-lg border bg-basic-dark-blue ${borderColor}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-basic-white">{companyName}</span>
          <PipelineProgressSummary
            phases={phases}
            progressPercent={progressPercent}
          />
          <span className="px-2 py-0.5 text-xs rounded bg-basic-blue text-basic-grey-inactive">
            {totalEvents} events
          </span>
          <StatusBadge
            status={overallStatus}
            currentStage={currentStage}
            failedStage={failedStage}
          />
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 transition-colors rounded hover:bg-basic-blue"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          <svg
            className={`w-5 h-5 transition-transform text-basic-grey-inactive ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Progress Bar */}
      <PipelineProgressBar phases={phases} progressPercent={progressPercent} />

      {/* Last Activity */}
      {lastEventAt && (
        <div className="mt-2 text-xs text-basic-grey-inactive">
          Last activity: {new Date(lastEventAt).toLocaleTimeString()}
        </div>
      )}

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 space-y-3">
          {phases.map(phase => (
            <div
              key={phase.phase}
              className="border rounded border-basic-blue overflow-hidden"
            >
              {/* Phase header */}
              <div
                className={`px-3 py-2 flex justify-between items-center ${getPhaseHeaderBgColor(phase.status)}`}
              >
                <span className="font-medium text-basic-white">
                  {phase.label}
                </span>
                <span
                  className={`text-xs ${getPhaseStatusTextColor(phase.status)}`}
                >
                  {getPhaseStatusLabel(phase.status)}
                </span>
              </div>
              {/* Events table */}
              <table className="w-full text-sm">
                <tbody>
                  {phase.events.map((event, idx) => (
                    <tr
                      key={event.event}
                      className={
                        idx % 2 === 0 ? "bg-basic-dark-blue" : "bg-basic-black"
                      }
                    >
                      <td className="px-3 py-2 text-basic-white w-8">
                        <EventStatusIcon status={event.status} />
                      </td>
                      <td className="px-3 py-2 text-basic-white">
                        {event.label}
                        {event.skipped && (
                          <span className="ml-2 text-xs text-basic-grey-inactive">
                            Skipped
                          </span>
                        )}
                        {event.expectedCount && event.expectedCount > 1 && (
                          <span className="ml-1 text-xs text-basic-grey-inactive">
                            (×{event.expectedCount})
                          </span>
                        )}
                        {event.connectors && (
                          <ul className="mt-2 space-y-2 text-xs text-basic-grey-inactive">
                            {event.connectors.length === 0 && (
                              <li>No connectors reported for this run</li>
                            )}
                            {event.connectors.map(connector => (
                              <li key={connector.connector_id}>
                                <div>
                                  <span className="text-basic-white">
                                    {connector.connector_name}
                                  </span>
                                  {" · "}
                                  {connector.source}
                                  {" · "}
                                  <span
                                    className={
                                      {
                                        failed: "text-states-error",
                                        success: "text-states-success",
                                        skipped: "",
                                        unknown: "",
                                      }[connector.status]
                                    }
                                  >
                                    {connector.status}
                                  </span>
                                </div>
                                <div className="break-all">
                                  {connector.connector_id}
                                </div>
                                {connector.failed_task_ids.length > 0 && (
                                  <div className="break-all text-states-error">
                                    Failed tasks:{" "}
                                    {connector.failed_task_ids.join(", ")}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-3 py-2 text-basic-grey-inactive text-right">
                        {event.eventAt
                          ? new Date(event.eventAt).toLocaleTimeString()
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-right w-20">
                        {event.successCount && (
                          <span className="text-states-success mr-1">
                            ✓{event.successCount}
                          </span>
                        )}
                        {event.failCount && (
                          <span className="text-states-error">
                            ✗{event.failCount}
                          </span>
                        )}
                        {!event.successCount && !event.failCount && (
                          <span className="text-basic-grey-inactive">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
